import { inputHasSteer, steerLinesOf } from "../lib/triageChatHost.js";

// Work staged in this chat, waiting for the person to steer it (an
// investigation, from the Reviews lens). The steering is the composer's
// text, one steer a line: checking a steer here puts it there as a line,
// where it can be edited like anything typed, and Start (or Enter there)
// starts the work on what the composer holds. Gone once it starts. Until the
// chat can send (`ready`: a chat back from storage is still loading) Start
// waits, as the composer does.
const EYEBROW = "text-[10px] uppercase leading-[1.2] tracking-[0.14em]";

export default function ChatSteering({ steering = null, input = "", busy = false, ready = true, onToggle, onStart }) {
    if (!steering || busy) return null;
    const steers = Array.isArray(steering.steers) ? steering.steers : [];
    const count = steerLinesOf(input).length;
    const known = new Set((steering.groups || []).map((g) => g.id));
    const groups = [
        ...(steering.groups || []).map((g) => [g.id, g.label, g.gloss, steers.filter((s) => s.group === g.id)]),
        ["", "", "", steers.filter((s) => !known.has(s.group))],
    ].filter(([, , , list]) => list.length);
    return (
        <section
            data-chat-steering=""
            aria-label={steering.title}
            className="mt-[8px] grid gap-[8px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px]"
        >
            <span className={`${EYEBROW} text-[color:var(--muted)]`}>{steering.title}</span>
            {groups.length ? (
                <div className="grid max-h-[50vh] gap-[8px] overflow-y-auto">
                    {groups.map(([key, label, gloss, list]) => (
                        <div key={key || "other"} data-chat-steer-group={key || "other"} className="grid gap-[5px]">
                            {label ? (
                                <span className="flex flex-wrap items-baseline gap-x-[6px]">
                                    <span className={`${EYEBROW} text-[color:var(--muted-soft)]`}>{label}</span>
                                    {gloss ? <span className="text-[11px] leading-[1.2] text-[color:var(--muted-soft)]">{gloss}</span> : null}
                                </span>
                            ) : null}
                            {list.map((s) => (
                                <label key={s.id} data-chat-steer={s.id} className="grid cursor-pointer items-start gap-x-[8px] [grid-template-columns:auto_minmax(0,1fr)]">
                                    <input
                                        type="checkbox"
                                        checked={inputHasSteer(input, s.text)}
                                        onChange={() => onToggle?.(s.id)}
                                        className="mt-[2px] cursor-pointer [accent-color:var(--ink)]"
                                    />
                                    <span className="grid gap-[1px]">
                                        <span className="text-[12px] leading-[1.35] text-[color:var(--ink)]">{s.text}</span>
                                        {s.why ? <span className="text-[11px] leading-[1.35] text-[color:var(--muted)]">{s.why}</span> : null}
                                    </span>
                                </label>
                            ))}
                        </div>
                    ))}
                </div>
            ) : null}
            {steering.note ? <span className="text-[11px] leading-[1.35] text-[color:var(--muted)]">{steering.note}</span> : null}
            <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[4px]">
                <button
                    type="button"
                    data-chat-steering-start=""
                    disabled={!ready}
                    onClick={() => onStart?.()}
                    className="cursor-pointer rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--control-bg)] px-[10px] py-[4px] text-[11px] uppercase leading-[1.08] tracking-[0.04em] text-[color:var(--ink)] transition-colors hover:border-[color:var(--ink)] disabled:cursor-default disabled:opacity-50 disabled:hover:border-[color:var(--line-strong)]"
                >
                    {count ? `${steering.startLabel} with ${count} steer${count === 1 ? "" : "s"}` : steering.startLabel}
                </button>
                {ready
                    ? <span className="text-[11px] leading-[1.3] text-[color:var(--muted-soft)]">Checked steers are lines in the box below: edit them, or add your own.</span>
                    : <span data-chat-steering-loading="" className="text-[11px] leading-[1.3] text-[color:var(--muted)] motion-safe:animate-pulse">Loading the chat&hellip;</span>}
            </div>
        </section>
    );
}
